/**
 * RateLimitsPanel — platform-root surface for overriding per-endpoint rate caps
 * at runtime. Each row shows the effective value, its source (default/env/override),
 * and an inline Edit form that accepts slowapi expressions like "5/minute".
 *
 * Backend contract:
 *   GET    /api/platform/rate-limits
 *   PUT    /api/platform/rate-limits/{name}   body: {value:"5/minute"}
 *   DELETE /api/platform/rate-limits/{name}
 */
import React, { useEffect, useState } from 'react';
import GlassSelect from './GlassSelect';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = ['second', 'minute', 'hour', 'day'];

const ROW_LABELS = {
  login: 'Login attempts',
  invite_create: 'Invites created',
  invite_accept: 'Invite accept',
  scan: 'Scout (single)',
  scan_batch: 'Scout batch',
  fixer: 'Fixer roadmap',
  scheduler: 'Schedule mutations',
  tracker_run: 'Tracker scan',
};

function sourceBadge(source) {
  const colors = {
    override: { bg: '#fef3c7', color: '#92400e' },
    env: { bg: '#dbeafe', color: '#1e3a8a' },
    default: { bg: '#f3f4f6', color: '#4b5563' },
  };
  const s = colors[source] || colors.default;
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase',
      letterSpacing: '0.05em', background: s.bg, color: s.color, fontWeight: 600,
    }}>{source}</span>
  );
}

function parseExpr(expr) {
  if (!expr || !expr.includes('/')) return { count: '', unit: 'minute' };
  const [count, unit] = expr.split('/');
  return { count: count.trim(), unit: unit.trim() };
}

function RateRow({ row, onSave, onClear, busy }) {
  const [editing, setEditing] = useState(false);
  const [count, setCount] = useState(() => parseExpr(row.effective).count);
  const [unit, setUnit] = useState(() => parseExpr(row.effective).unit);
  const [err, setErr] = useState(null);

  const startEdit = () => {
    const p = parseExpr(row.effective);
    setCount(p.count); setUnit(p.unit); setErr(null); setEditing(true);
  };

  const save = async () => {
    setErr(null);
    const n = parseInt(count, 10);
    if (!n || n < 1) { setErr('Count must be a positive integer'); return; }
    try {
      await onSave(row.name, `${n}/${unit}`);
      setEditing(false);
    } catch (e) { setErr(e.message); }
  };

  return (
    <tr data-testid={`rate-row-${row.name}`}>
      <td style={{ fontWeight: 600, fontSize: 13 }}>
        {ROW_LABELS[row.name] || row.name}
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 400 }}>
          {row.name}
        </div>
      </td>
      <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>
        {editing ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              type="number" min={1} value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid={`rate-count-${row.name}`}
              style={{ width: 60, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13 }}
            />
            <span>/</span>
            <GlassSelect
              value={unit} onChange={(e) => setUnit(e.target.value)}
              data-testid={`rate-unit-${row.name}`}
              style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13 }}
            >
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </GlassSelect>
          </div>
        ) : (
          <span data-testid={`rate-effective-${row.name}`}>{row.effective}</span>
        )}
      </td>
      <td>{sourceBadge(row.source)}</td>
      <td style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: "'IBM Plex Mono', monospace" }}>
        default: {row.default}
        {row.env && <div>env: {row.env}</div>}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}
                    data-testid={`rate-save-${row.name}`}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setEditing(false)}
                    style={{ marginLeft: 4 }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-outline btn-sm" onClick={startEdit}
                    data-testid={`rate-edit-${row.name}`}>
              Edit
            </button>
            {row.source === 'override' && (
              <button className="btn btn-outline btn-sm"
                      onClick={() => onClear(row.name)}
                      style={{ marginLeft: 4 }}
                      data-testid={`rate-clear-${row.name}`}>
                Reset
              </button>
            )}
          </>
        )}
        {err && <div className="auth-error" style={{ marginTop: 4 }}>{err}</div>}
      </td>
    </tr>
  );
}

export default function RateLimitsPanel() {
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/platform/rate-limits`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLimits(data.limits || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveLimit = async (name, value) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/rate-limits/${name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(err.detail) || 'Failed to save');
      }
      await load();
    } finally { setBusy(false); }
  };

  const clearLimit = async (name) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/platform/rate-limits/${name}`, { method: 'DELETE' });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="platform-rate-limits-panel">
      <div className="panel-header">Rate limits</div>
      <div className="panel-body" style={{ padding: 0 }}>
        {loading && <div style={{ padding: 16 }}>Loading rate limits…</div>}
        {error && (
          <div className="auth-error" style={{ margin: 12 }} data-testid="rate-limits-error">{error}</div>
        )}
        {!loading && !error && (
          <>
            <div style={{
              padding: '8px 16px', fontSize: 11, color: 'var(--text-secondary)',
              borderBottom: '1px solid var(--border)',
            }}>
              Overrides take effect immediately and survive restart. Platform Root only.
            </div>
            <div className="data-table-wrap">
              <table className="data-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Effective</th>
                    <th>Source</th>
                    <th>Fallback</th>
                    <th style={{ width: 180 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {limits.map((r) => (
                    <RateRow key={r.name} row={r} busy={busy}
                             onSave={saveLimit} onClear={clearLimit} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
